import type { AudioClip, FadeCurve, Project } from '@/domain/types';
import { clearCrossfade, joinNearest, planCrossfade } from '@/domain/crossfade';
import { bareOverlaps, overlapSeconds } from '@/domain/overlap';
import { defineCommand } from './registry';
import { enumParam, idParam, idsParam, intParam, numberParam, omitted } from './params';

/**
 * Creating and removing crossfades.
 *
 * A crossfade is written as two ordinary clip fades, so these commands only
 * ever change clips — nothing downstream needs to know the concept exists.
 *
 * Both commands accept either an explicit pair of clips or a position on a
 * track, because those are the two ways a user asks for one: with a selection,
 * or with the playhead parked on an edit.
 */

const CURVES: readonly FadeCurve[] = ['linear', 'equalPower', 'exponential'];

interface JoinTarget {
  clipIds: string[] | null;
  trackId: string | null;
  tick: number;
  takeLane: number;
}

function parseTarget(raw: Record<string, unknown>): JoinTarget | null {
  const clipIds = omitted(raw.clipIds) ? null : idsParam(raw.clipIds);
  if (clipIds && clipIds.length !== 2) return null;

  const trackId = omitted(raw.trackId) ? null : idParam(raw.trackId);
  if (!clipIds && !trackId) return null;

  const tick = intParam(raw.tick, 0, Number.MAX_SAFE_INTEGER, 0);
  const takeLane = intParam(raw.takeLane, 0, 64, 0);
  if (tick === null || takeLane === null) return null;

  return { clipIds, trackId, tick, takeLane };
}

/** Resolves a target to the two clips forming a join, if there is one. */
function resolveJoin(project: Project, target: JoinTarget): { outgoing: AudioClip; incoming: AudioClip } | null {
  if (target.clipIds) {
    const [first, second] = target.clipIds.map((id) => project.clips[id]);
    if (!first || !second || first.kind !== 'audio' || second.kind !== 'audio') return null;
    return first.startTicks <= second.startTicks
      ? { outgoing: first, incoming: second }
      : { outgoing: second, incoming: first };
  }
  return target.trackId ? joinNearest(project, target.trackId, target.tick, target.takeLane) : null;
}

export const crossfadeClips = defineCommand<JoinTarget & { durationSec: number | null; curve: FadeCurve | null }>({
  type: 'clip.crossfade',
  label: () => 'Crossfade',
  parse: (raw) => {
    const target = parseTarget(raw);
    if (!target) return null;
    const durationSec = omitted(raw.durationSec) ? null : numberParam(raw.durationSec, 0.001, 10);
    if (!omitted(raw.durationSec) && durationSec === null) return null;
    return { ...target, durationSec, curve: enumParam(raw.curve, CURVES) };
  },
  apply: (project, p) => {
    const join = resolveJoin(project, p);
    if (!join) return project;

    // Falling back to the project's own setting means the toolbar length and a
    // comp boundary produce the same fade, rather than two different defaults.
    const requested = p.durationSec ?? Math.max(0.005, project.transport.crossfadeSec);
    const result = planCrossfade(project, join.outgoing, join.incoming, requested, p.curve ?? 'equalPower');
    if (!result.ok) return project;

    return {
      ...project,
      clips: {
        ...project.clips,
        [result.plan.outgoing.id]: result.plan.outgoing,
        [result.plan.incoming.id]: result.plan.incoming,
      },
    };
  },
});

export const removeCrossfade = defineCommand<JoinTarget>({
  type: 'clip.removeCrossfade',
  label: () => 'Remove crossfade',
  parse: (raw) => parseTarget(raw),
  apply: (project, p) => {
    const join = resolveJoin(project, p);
    if (!join) return project;
    if (join.outgoing.fadeOut.durationSec <= 0 && join.incoming.fadeIn.durationSec <= 0) return project;

    // The overlap goes with the fades: leaving it would have both clips playing
    // at full level across the join, which is louder than either the crossfade
    // or the cut it replaced.
    const cleared = clearCrossfade(project, join.outgoing, join.incoming);
    return {
      ...project,
      clips: { ...project.clips, [cleared.outgoing.id]: cleared.outgoing, [cleared.incoming.id]: cleared.incoming },
    };
  },
});

/**
 * Crossfades every overlap that nothing is covering.
 *
 * Not the default crossfade length — **the whole overlap**. A 10ms fade dropped
 * into a 400ms overlap would leave 380ms of two clips at full level, which is
 * the problem rather than the fix. The two clips already sit on top of each
 * other for exactly that long, so there is material for a fade of exactly that
 * length and no edge has to move.
 *
 * That is also why this is a separate command from `crossfadeClips` rather than
 * a call to it with a big number: the length is not a preference here, it is a
 * fact about the geometry, and a command whose meaning is "make this join
 * inaudible" should not take a number that could contradict it.
 *
 * Order is immaterial, and that is worth saying rather than defending against.
 * Extending a clip into its neighbour is how `planCrossfade` handles a *butt
 * join*, where there is no overlap to fade across. A bare overlap has one by
 * definition, so only the two fades change and no edge moves — which is what
 * lets a row of overlaps be resolved in any order at all. A test asserts that
 * nothing moved, so this is a property rather than an assumption.
 */
export const fadeBareOverlaps = defineCommand<{ trackIds: string[] | null; curve: FadeCurve | null }>({
  type: 'crossfade.fadeOverlaps',
  label: () => 'Crossfade overlaps',
  parse: (raw) => {
    const trackIds = omitted(raw.trackIds) || raw.trackIds === null ? null : idsParam(raw.trackIds);
    if (raw.trackIds !== undefined && raw.trackIds !== null && trackIds === null) return null;
    const curve = enumParam(raw.curve, CURVES);
    if (raw.curve !== undefined && raw.curve !== null && curve === null) return null;
    return { trackIds, curve };
  },
  apply: (project, p) => {
    const wanted = p.trackIds ? new Set(p.trackIds) : null;
    const found = bareOverlaps(project).filter((o) => !wanted || wanted.has(o.trackId));
    if (found.length === 0) return project;

    let next = project;
    for (const overlap of found) {
      const outgoing = next.clips[overlap.outgoingId];
      const incoming = next.clips[overlap.incomingId];
      if (!outgoing || !incoming) continue;
      // Ask for more than the overlap can be: planCrossfade caps it at what is
      // actually there, so this says "all of it" without naming a number.
      const result = planCrossfade(next, outgoing, incoming, overlapSeconds(next, overlap), p.curve ?? 'equalPower');
      if (!result.ok) continue;
      next = {
        ...next,
        clips: {
          ...next.clips,
          [result.plan.outgoing.id]: result.plan.outgoing,
          [result.plan.incoming.id]: result.plan.incoming,
        },
      };
    }
    return next;
  },
});

export const setDefaultCrossfade = defineCommand<{ seconds: number }>({
  type: 'transport.setCrossfade',
  label: (p) => (p.seconds <= 0 ? 'Turn off automatic crossfades' : `Set crossfade to ${(p.seconds * 1000).toFixed(0)} ms`),
  parse: (raw) => {
    const seconds = numberParam(raw.seconds, 0, 10);
    return seconds === null ? null : { seconds };
  },
  coalesceKey: () => 'crossfadeLength',
  apply: (project, p) =>
    project.transport.crossfadeSec === p.seconds
      ? project
      : { ...project, transport: { ...project.transport, crossfadeSec: p.seconds } },
});

/**
 * Which half of a join a shape applies to.
 *
 * `both` is the default and keeps the two sides mirror images, which is what
 * holds the level constant across the join. The single-sided values exist
 * because that is not always what is wanted — holding a decaying note a little
 * longer under the start of the next one is a real edit — and they deliberately
 * give up the constant-power guarantee to do it. The UI says so; the command
 * does not pretend otherwise.
 */
export type CrossfadeSide = 'both' | 'out' | 'in';

const SIDES: readonly CrossfadeSide[] = ['both', 'out', 'in'];

export const setCrossfadeShape = defineCommand<
  JoinTarget & { curvature: number; curve: FadeCurve | null; side: CrossfadeSide }
>({
  type: 'clip.setCrossfadeShape',
  label: (p) =>
    p.side === 'both' ? 'Shape crossfade' : p.side === 'out' ? 'Shape the outgoing half' : 'Shape the incoming half',
  parse: (raw) => {
    const target = parseTarget(raw);
    const curvature = numberParam(raw.curvature, -1, 1);
    if (!target || curvature === null) return null;
    // Absent means both halves; *unknown* is refused rather than falling back to
    // them. Quietly shaping both when one was asked for is exactly the kind of
    // silent substitution a command boundary exists to prevent.
    const side = raw.side === undefined ? 'both' : enumParam<CrossfadeSide>(raw.side, SIDES);
    if (!side) return null;
    return { ...target, curvature, curve: enumParam(raw.curve, CURVES), side };
  },
  coalesceKey: (p) => `xfadeShape:${p.side}:${p.clipIds?.join(',') ?? `${p.trackId}:${p.tick}`}`,
  apply: (project, p) => {
    const join = resolveJoin(project, p);
    if (!join) return project;
    if (join.outgoing.fadeOut.durationSec <= 0 || join.incoming.fadeIn.durationSec <= 0) return project;

    const curve = p.curve ?? join.outgoing.fadeOut.curve;
    const clips = { ...project.clips };

    if (p.side !== 'in') {
      clips[join.outgoing.id] = {
        ...join.outgoing,
        fadeOut: { ...join.outgoing.fadeOut, curve, curvature: p.curvature },
      };
    }
    if (p.side !== 'out') {
      clips[join.incoming.id] = {
        ...join.incoming,
        fadeIn: { ...join.incoming.fadeIn, curve, curvature: p.curvature },
      };
    }
    return { ...project, clips };
  },
});
